import { getLocalStorageValue, removeLocalStorageValue, setLocalStorageValue } from 'src/utils/local-storage';

import type { SavedDataTableView } from './types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function createSavedViewsArrayValidator<TFilters>(filtersValidator: (value: unknown) => value is TFilters) {
  return (value: unknown): value is SavedDataTableView<TFilters>[] => {
    if (!Array.isArray(value)) return false;
    return value.every((entry) => {
      if (!isRecord(entry)) return false;
      if (typeof entry['id'] !== 'string') return false;
      if (typeof entry['name'] !== 'string') return false;
      if (typeof entry['createdAt'] !== 'number') return false;
      return filtersValidator(entry['filters']);
    });
  };
}

export function readSavedDataTableViews<TFilters>({
  storageKey,
  filtersValidator,
}: {
  storageKey: string;
  filtersValidator: (value: unknown) => value is TFilters;
}) {
  return (
    getLocalStorageValue({
      dataKey: storageKey,
      validator: createSavedViewsArrayValidator(filtersValidator),
    }) ?? []
  );
}

export function writeSavedDataTableViews<TFilters>({
  storageKey,
  savedViews,
}: {
  storageKey: string;
  savedViews: SavedDataTableView<TFilters>[];
}) {
  setLocalStorageValue({ dataKey: storageKey, data: savedViews });
}

export function clearSavedDataTableViews(storageKey: string) {
  removeLocalStorageValue(storageKey);
}

export function getActiveDataTableViewId(storageKey: string) {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(storageKey);
}

export function setActiveDataTableViewId({ storageKey, id }: { storageKey: string; id: string | null }) {
  if (id === null) {
    removeLocalStorageValue(storageKey);
    return;
  }
  if (typeof window === 'undefined') return;
  localStorage.setItem(storageKey, id);
}
